import { useCallback, useEffect, useRef } from 'react';
import { SearchProvider } from '../../contexts/SearchProvider';
import shopify from '../../../js/shopify';
import helpers from '../../../js/helpers';
import api from '../../../js/api';

/**
 * Debounce helper function to limit how often a function can be called
 * @param {Function} func - The function to debounce
 * @param {number} wait - The debounce delay in milliseconds
 * @return {Function} The debounced function
 */
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Parse sort options from a string to an array of objects
 * @param {string} sortOptionsString - String containing sort options (format: key:label\nkey:label)
 * @returns {Array} Array of sort options objects with key and label properties
 */
function parseSortOptions(sortOptionsString) {
  if (!sortOptionsString) {
    return [
      { key: 'relevance_desc', label: 'Relevance' },
      { key: 'created_at_desc', label: 'Newest' },
      { key: 'best_selling_desc', label: 'Popular' },
      { key: 'price_asc', label: 'Price - Low to High' },
      { key: 'price_desc', label: 'Price - High to Low' },
      { key: 'manual_asc', label: 'Featured' },
    ];
  }
  
  return sortOptionsString.split('\n')
    .map(option => {
      const [key, label] = option.split(':');
      return { key, label };
    })
    .filter(option => option.key && option.label);
}

/**
 * Parse facets from a string to an array
 * @param {string} facetsString - String containing facets (format: facet1\nfacet2\nfacet3)
 * @returns {Array} Array of facet names
 */
function parseFacets(facetsString) {
  if (!facetsString) return [];
  return facetsString.split('\n').map(facet => facet.trim()).filter(Boolean);
}

/**
 * Creates a provider for Boost Search API implementation
 * 
 * @param {Object} props - The props for the BoostSearchProvider component
 * @param {React.ReactNode} props.children - Child components
 * @param {Object} props.settings - Settings for the Boost search
 * @param {string} props.searchType - Type of search ('search', 'plp', 'header')
 * @returns {JSX.Element} A SearchProvider with Boost-specific implementation
 */
export function BoostSearchProvider({ children, settings, searchType = 'search' }) {
  // Create refs to maintain state across re-renders
  const providerRef = useRef({
    initialized: false,
    currentSort: 'relevance',
    currentDirection: 'desc',
    currentFilters: {},
    currentPage: 1,
    currentCollectionSize: 0,
    currentCollection: 'all',
    currentResults: 0,
    searchQuery: '',
    productsPerPage: 24,
    isProcessingRequest: false,
    debouncedSearchRequest: null,
    boostApiKey: null,
    apiEndpoint: 'https://services.mybcapps.com/bc-sf-filter',
    sortOptions: [],
    availableFacets: [],
    filtersHideOutOfStock: true,
    resultsReturnedHeader: 12,
  });

  const provider = providerRef.current;

  // Initialize the provider
  const initialize = useCallback((searchSettings) => {
    if (provider.initialized) return;

    // console.log('Boost search provider initialized', searchSettings);
    
    // Parse and set settings with defaults
    provider.productsPerPage = parseInt(searchSettings?.products_per_page, 10) || 24;
    provider.currentSort = searchSettings?.default_sort || 'relevance';
    provider.currentDirection = searchSettings?.default_sort_direction || 'desc';
    provider.shop = searchSettings?.store || '';
    provider.apiEndpoint = searchSettings?.api_endpoint || 'https://services.mybcapps.com/bc-sf-filter';
    provider.sortOptions = parseSortOptions(searchSettings?.sort_options);
    provider.availableFacets = parseFacets(searchSettings?.facets);
    provider.filtersHideOutOfStock = searchSettings?.filters_hide_out_of_stock !== false; // default to true
    provider.resultsReturnedHeader = parseInt(searchSettings?.results_returned_header, 10) || 12;
    provider.initialized = true;

    // Create debounced search function with 300ms delay
    provider.debouncedSearchRequest = debounce(async (detail) => {
      const { query, type, collection,collectionId, collectionSize, productsPerPage } = detail;

      // console.log('Debounced search request processing', provider.isProcessingRequest);
      if (provider.isProcessingRequest) return;
      provider.isProcessingRequest = true;

      provider.type = type;

      if (type === 'plp') {
        // Collection search
        provider.currentCollection = collection;
        provider.currentCollectionId = collectionId;
        provider.currentCollectionSize = collectionSize;

        await makeBoostCollectionRequest();
      } else {
        // Query search
        provider.searchQuery = query;
        provider.currentCollectionSize = collectionSize;

        await makeBoostSearchRequest();
      }

      provider.isProcessingRequest = false;
    }, 300); // 300ms debounce delay

    // Set up event listeners
    window.addEventListener('searchrequest', handleSearchRequest);
    window.addEventListener('searchrequest-sort-applied', handleSortChange);
    window.addEventListener('searchrequest-filter-applied', handleFilterApplied);
    window.addEventListener('nextpage', handleNextPage);
    window.addEventListener('searchrequest-sortchanged', handleSortChange);

    return () => {
      // window.removeEventListener('searchrequest', handleSearchRequest);
      // window.removeEventListener('searchrequest-sort-applied', handleSortChange);
      // window.removeEventListener('searchrequest-filter-applied', handleFilterApplied);
      // window.removeEventListener('nextpage', handleNextPage);
      // window.removeEventListener('searchrequest-sortchanged', handleSortChange);
    };
  }, []);

  // Handle search requests
  const handleSearchRequest = useCallback((event) => {
    // Use the debounced search function instead of processing immediately
    if (provider.debouncedSearchRequest) {
      provider.debouncedSearchRequest(event.detail);
    }
  }, []);

  // Handle sort changes
  const handleSortChange = useCallback(async (event) => {
    const { sortOption } = event.detail;

    if (provider.isProcessingRequest) return;
    provider.isProcessingRequest = true;

    // Find the sort option in our configured sort options
    const foundSortOption = provider.sortOptions.find(option => option.label === sortOption);
    
    if (foundSortOption) {
      const [sortField, direction] = foundSortOption.key.split('_');
      provider.currentSort = sortField;
      provider.currentDirection = direction || 'desc';
    } else {
      // Map sort options to Boost API format as fallback
      let sort = 'relevance';
      let direction = 'desc';

      switch (sortOption) {
        case 'Newest':
          sort = 'created_at';
          direction = 'desc';
          break;
        case 'Popular':
          sort = 'best_selling';
          direction = 'desc';
          break;
        case 'LowToHigh':
        case 'Price - Low to High':
          sort = 'price';
          direction = 'asc';
          break;
        case 'HighToLow':
        case 'Price - High to Low':
          sort = 'price';
          direction = 'desc';
          break;
        case 'Featured':
          sort = 'manual';
          direction = 'asc';
          break;
      }

      provider.currentSort = sort;
      provider.currentDirection = direction;
    }
    
    provider.currentPage = 1; // Reset to first page

    if (provider.searchQuery) {
      await makeBoostSearchRequest();
    } else {
      await makeBoostCollectionRequest();
    }

    provider.isProcessingRequest = false;
  }, []);

  // Handle filter changes
  const handleFilterApplied = useCallback(async (event) => {
    const { filters_object } = event.detail;

    if (provider.isProcessingRequest) return;
    provider.isProcessingRequest = true;

    // Save the filters object as is - Boost API expects a specific format
    provider.currentFilters = filters_object || {};
    provider.currentPage = 1; // Reset to first page

    if (provider.searchQuery) {
      await makeBoostSearchRequest();
    } else {
      await makeBoostCollectionRequest();
    }

    provider.isProcessingRequest = false;
  }, []);

  // Handle pagination requests
  const handleNextPage = useCallback(async (event) => {
    const { page } = event.detail;

    if (provider.isProcessingRequest) return;
    provider.isProcessingRequest = true;

    provider.currentPage = page;

    if (provider.searchQuery) {
      await makeBoostSearchRequest();
    } else {
      await makeBoostCollectionRequest();
    }

    provider.isProcessingRequest = false;
  }, []);

  // Transform Boost product to match our format
  const transformBoostProduct = useCallback((boostProduct) => {
    if (!boostProduct) return null;

    let images = [];

    // Check if boostProduct.images is a non-null object
    if (boostProduct.images && typeof boostProduct.images === 'object' && !Array.isArray(boostProduct.images)) {
      // Get the values (image URLs) from the object
      images = Object.values(boostProduct.images)
        // Filter out any non-string or empty values
        .filter(image => typeof image === 'string' && image)
        // Map to the desired structure
        .map(image => ({ src: image }));
    } else if (Array.isArray(boostProduct.images)) {
      // Handle case where it might sometimes be an array (fallback)
       images = boostProduct.images
        .filter(image => typeof image === 'string' && image)
        .map(image => ({ src: image }));
    }

    // Map Boost product format to our app's expected format
    return {
      id: boostProduct.id,
      title: boostProduct.title,
      handle: boostProduct.handle,
      tags: boostProduct.tags || [],
      product_type: boostProduct.product_type,
      vendor: boostProduct.vendor,
      featured_image: boostProduct.featured_image || boostProduct.image || '',
      images: images, // Use the processed images array
      available: boostProduct.available,
      price: boostProduct.price / 100, // Boost returns price in cents
      compare_at_price: boostProduct.compare_at_price ? boostProduct.compare_at_price / 100 : null,
      variants: boostProduct.variants?.map(variant => ({
        id: variant.id,
        title: variant.title,
        available: variant.available,
        price: variant.price / 100,
        compare_at_price: variant.compare_at_price ? variant.compare_at_price / 100 : null,
        options: variant.options || [],
        image: variant.image || null,
        sku: variant.sku || ''
      })) || [],
      options: boostProduct.options || [],
      metafields: boostProduct.metafields || [],
      created_at: boostProduct.created_at || new Date().toISOString(),
    };
  }, []);

  // Fetch collection products from Boost API
  const fetchProductsFromBoostCollection = useCallback(async () => {
    window.dispatchEvent(
      new CustomEvent('show-spinner', {
        detail: {
          id: 'spinner-collection',
        },
      })
    );

    console.log(provider);

    const endpoint = `${provider.apiEndpoint}/filter`;
    
    const params = new URLSearchParams({
      shop: provider.shop,
      collection_scope: provider.currentCollectionId,
      page: provider.currentPage,
      limit: provider.productsPerPage,
      sort: `${provider.currentSort}_${provider.currentDirection}`,
    });

    // Add filter for out of stock products if configured
    if (provider.filtersHideOutOfStock) {
      params.append('available', 'true');
    }

    // Add facet fields if configured
    if (provider.availableFacets && provider.availableFacets.length > 0) {
      params.append('facets', provider.availableFacets.join(','));
    }

    // Add filters if any
    if (Object.keys(provider.currentFilters).length > 0) {
      params.append('_filters', JSON.stringify(provider.currentFilters));
    }

    // Make the request
    try {
      const response = await fetch(`${endpoint}?${params.toString()}`);
      
      if (!response.ok) {
        throw new Error(`Boost API request failed: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching products from Boost collection:', error);
      return null;
    }
  }, []);

  // Fetch search results from Boost API
  const fetchProductsFromBoostSearch = useCallback(async () => {
    window.dispatchEvent(
      new CustomEvent('show-spinner', {
        detail: {
          id: 'spinner-collection',
        },
      })
    );

    const endpoint = `${provider.apiEndpoint}/search`;
    
    const params = new URLSearchParams({
      shop: provider.shop,
      q: provider.searchQuery,
      page: provider.currentPage,
      limit: provider.productsPerPage,
      sort: `${provider.currentSort}_${provider.currentDirection}`,
    });

    // Add filter for out of stock products if configured
    if (provider.filtersHideOutOfStock) {
      params.append('available', 'true');
    }

    // Add facet fields if configured
    if (provider.availableFacets && provider.availableFacets.length > 0) {
      params.append('facets', provider.availableFacets.join(','));
    }

    // Add filters if any
    if (Object.keys(provider.currentFilters).length > 0) {
      params.append('_filters', JSON.stringify(provider.currentFilters));
    }

    // Make the request
    try {
      const response = await fetch(`${endpoint}?${params.toString()}`);
      
      if (!response.ok) {
        throw new Error(`Boost API request failed: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching products from Boost search:', error);
      return null;
    }
  }, []);

  // Process collection results and dispatch events
  const makeBoostCollectionRequest = useCallback(async () => {
    const data = await fetchProductsFromBoostCollection();
    
    if (!data || !data.products) return;
    
    // Transform products to our format
    const products = data.products.map(transformBoostProduct).filter(Boolean);
    
    // Extract total results
    const totalResults = data.total_product || products.length;
    
    // Extract facets/filters
    const facets = transformBoostFilters(data.filters || []);

    // Dispatch filter list event
    window.dispatchEvent(
      new CustomEvent("filter-list", {
        detail: {
          type: "plp",
          filters: facets,
        },
      })
    );

    // Dispatch product list event
    window.dispatchEvent(
      new CustomEvent("product-list", {
        detail: {
          type: "plp",
          page: provider.currentPage,
          products: products,
          totalResults: totalResults,
        },
      })
    );
  }, [fetchProductsFromBoostCollection, transformBoostProduct]);

  // Process search results and dispatch events
  const makeBoostSearchRequest = useCallback(async () => {
    const data = await fetchProductsFromBoostSearch();

    if (!data || !data.products) return;
    
    // Transform products to our format
    const products = data.products.map(transformBoostProduct).filter(Boolean);
    
    // Extract total results
    const totalResults = data.total_product || products.length;
    
    // Extract facets/filters
    const facets = transformBoostFilters(data.filters || []);

    // Dispatch filter list event
    window.dispatchEvent(
      new CustomEvent("filter-list", {
        detail: {
          type: "search",
          filters: facets,
        },
      })
    );

    console.log('Dispatching searchrequest-products-complete event', totalResults);

    provider.isProcessingRequest = false;
    // Dispatch product list event
    window.dispatchEvent(
      new CustomEvent("searchrequest-products-complete", {
        detail: {
          query: provider.searchQuery,
          type: provider.type,
          page: provider.currentPage,
          products: products,
          totalResults: totalResults,
          filters: facets,
          resultsReturned: Math.min(provider.resultsReturnedHeader, products.length),
        },
      })
    );
  }, [fetchProductsFromBoostSearch, transformBoostProduct]);

  // Helper function to transform Boost filters to our format
  const transformBoostFilters = useCallback((boostFilters) => {
    if (!boostFilters || !Array.isArray(boostFilters)) return [];

    // If we have specified facets, only include those filters
    const availableFacets = provider.availableFacets;
    
    return boostFilters
      .filter(filter => 
        !availableFacets.length || 
        availableFacets.includes(filter.label.toLowerCase())
      )
      .map(filter => ({
        key: filter.label,
        label: filter.label,
        values: filter.values.map(value => ({
          key: value.key || value.label,
          label: value.label,
          count: value.doc_count || 0
        }))
      }));
  }, []);

  // Create the provider object that our base SearchProvider will use
  const boostProvider = {
    initialize,
    handleSearchRequest,
    handleSortChange,
    handleFilterApplied,
    handleNextPage,
  };

  return (
    <SearchProvider provider={boostProvider} settings={settings} searchType={searchType}>
      {children}
    </SearchProvider>
  );
}

export default BoostSearchProvider;
